/**
 * Observable event emitter implementation.
 *
 * Provides subscription, one-time subscription, unsubscription, and synchronous dispatch
 * for events defined by an {@link EventMap}.
 *
 * @typeParam X - The event map describing event names and payload tuples
 * @public
 */ class Observable {
    /** @internal */ _listeners;
    /**
   * Creates an {@link Observable}.
   */ constructor(){
        this._listeners = null;
    }
    /**
   * {@inheritDoc IEventTarget.on}
   */ on(type, listener, context) {
        if (listener) {
            this._listeners = this._internalAddEventListener(this._listeners, type, listener, {
                context: context ?? null,
                once: false
            });
        } else {
            console.error('Cannot set NULL listener');
        }
    }
    /**
   * {@inheritDoc IEventTarget.once}
   */ once(type, listener, context) {
        if (listener) {
            this._listeners = this._internalAddEventListener(this._listeners, type, listener, {
                context: context ?? null,
                once: true
            });
        } else {
            console.error('Cannot set NULL listener');
        }
    }
    /**
   * {@inheritDoc IEventTarget.off}
   */ off(type, listener, context) {
        if (listener) {
            this._internalRemoveEventListener(this._listeners, type, listener, context ?? null);
        }
    }
    /**
   * {@inheritDoc IEventTarget.dispatchEvent}
   */ dispatchEvent(type, ...args) {
        this._invokeLocalListeners(type, ...args);
    }
    /**
   * Adds an event listener to the given map.
   *
   * If the map is `null`, a new one will be created.
   *
   * @typeParam K - The event key within the event map
   * @param listenerMap - The current listener map
   * @param type - The event type to listen for
   * @param listener - The listener callback
   * @param options - Additional listener options
   * @returns The updated listener map
   * @internal
   */ _internalAddEventListener(listenerMap, type, listener, options) {
        if (typeof type !== 'string') {
            return listenerMap;
        }
        if (!listenerMap) {
            listenerMap = {};
        }
        const l = listener;
        const o = {
            ...options
        };
        let handlers = listenerMap[type];
        if (!handlers) {
            listenerMap[type] = handlers = [];
        }
        handlers.push({
            handler: l,
            options: o,
            removed: false
        });
        return listenerMap;
    }
    /**
   * Removes an event listener from the given map.
   *
   * A listener is removed only if both the function reference and the context match.
   *
   * @typeParam K - The event key within the event map
   * @param listenerMap - The current listener map
   * @param type - The event type to remove from
   * @param listener - The listener callback to remove
   * @param context - The context object that must match the one used when adding
   * @internal
   */ _internalRemoveEventListener(listenerMap, type, listener, context) {
        if (typeof type !== 'string' || !listenerMap) {
            return;
        }
        const l = listener;
        const handlers = listenerMap[type];
        if (handlers) {
            for(let i = 0; i < handlers.length; i++){
                const handler = handlers[i];
                if (handler.handler === l && handler.options.context === context) {
                    handlers.splice(i, 1);
                    break;
                }
            }
            if (handlers.length === 0) {
                listenerMap[type] = undefined;
            }
        }
    }
    /**
   * Invokes local listeners for a given event type with the provided arguments.
   *
   * Listeners added with `once: true` are marked and pruned after invocation.
   *
   * @typeParam K - The event key within the event map
   * @param type - The event type to invoke
   * @param args - The payload to pass to each listener
   * @internal
   */ _invokeLocalListeners(type, ...args) {
        if (!this._listeners) {
            return;
        }
        const handlers = this._listeners[type];
        if (handlers && handlers.length > 0) {
            const handlersCopy = handlers.slice();
            for (const handler of handlersCopy){
                handler.handler.call(handler.options?.context || this, ...args);
                if (handler.options.once) {
                    handler.removed = true;
                }
            }
            for(let i = handlers.length - 1; i >= 0; i--){
                if (handlers[i].removed) {
                    handlers.splice(i, 1);
                }
            }
        }
    }
}
/**
 * Mixin that augments a class with {@link IEventTarget} capabilities.
 *
 * It returns a higher-order factory that, when instantiated with an event map `X`,
 * produces a subclass adding `on/once/off/dispatchEvent` and the internal listener management.
 *
 * Usage:
 * ```ts
 * class Base {}
 * const Eventful = makeObservable(Base)<{ 'ready': []; 'data': [number] }>();
 * const obj = new Eventful();
 * obj.on('data', (n) => console.log(n));
 * obj.dispatchEvent('data', 42);
 * ```
 *
 * @typeParam C - A constructor type to be extended (plain `ObjectConstructor` or a generic class)
 * @param cls - The base class to extend
 * @returns A generic factory that accepts an event map and returns an extended class
 * @public
 */ function makeObservable(cls) {
    return function _() {
        return class E extends cls {
            /** @internal */ _listeners;
            constructor(...args){
                super(...args);
                this._listeners = null;
            }
            /**
       * {@inheritDoc IEventTarget.on}
       */ on(type, listener, context) {
                if (listener) {
                    this._listeners = this._internalAddEventListener(this._listeners, type, listener, {
                        context: context ?? null,
                        once: false
                    });
                } else {
                    console.error('Cannot set NULL listener');
                }
            }
            /**
       * {@inheritDoc IEventTarget.once}
       */ once(type, listener, context) {
                if (listener) {
                    this._listeners = this._internalAddEventListener(this._listeners, type, listener, {
                        context: context ?? null,
                        once: true
                    });
                } else {
                    console.error('Cannot set NULL listener');
                }
            }
            /**
       * {@inheritDoc IEventTarget.off}
       */ off(type, listener, context) {
                this._internalRemoveEventListener(this._listeners, type, listener, context ?? null);
            }
            /**
       * {@inheritDoc IEventTarget.dispatchEvent}
       */ dispatchEvent(type, ...args) {
                this._invokeLocalListeners(type, ...args);
            }
            /**
       * Adds an event listener to the given map.
       *
       * If the map is `null`, a new one will be created.
       *
       * @typeParam K - The event key within the event map
       * @param listenerMap - The current listener map
       * @param type - The event type to listen for
       * @param listener - The listener callback
       * @param options - Additional listener options
       * @returns The updated listener map
       * @internal
       */ _internalAddEventListener(listenerMap, type, listener, options) {
                if (typeof type !== 'string') {
                    return listenerMap;
                }
                if (!listenerMap) {
                    listenerMap = {};
                }
                const l = listener;
                const o = {
                    ...options
                };
                let handlers = listenerMap[type];
                if (!handlers) {
                    listenerMap[type] = handlers = [];
                }
                handlers.push({
                    handler: l,
                    options: o,
                    removed: false
                });
                return listenerMap;
            }
            /**
       * Removes an event listener from the given map.
       *
       * A listener is removed only if both the function reference and the context match.
       *
       * @typeParam K - The event key within the event map
       * @param listenerMap - The current listener map
       * @param type - The event type to remove from
       * @param listener - The listener callback to remove
       * @param context - The context object that must match the one used when adding
       * @internal
       */ _internalRemoveEventListener(listenerMap, type, listener, context) {
                if (typeof type !== 'string' || !listenerMap) {
                    return;
                }
                const l = listener;
                const handlers = listenerMap[type];
                if (handlers) {
                    for(let i = 0; i < handlers.length; i++){
                        const handler = handlers[i];
                        if (handler.handler === l && handler.options.context === context) {
                            handlers.splice(i, 1);
                            break;
                        }
                    }
                    if (handlers.length === 0) {
                        listenerMap[type] = undefined;
                    }
                }
            }
            /**
       * Invokes local listeners for a given event type with the provided arguments.
       *
       * Listeners added with `once: true` are marked and pruned after invocation.
       *
       * @typeParam K - The event key within the event map
       * @param type - The event type to invoke
       * @param args - The payload to pass to each listener
       * @internal
       */ _invokeLocalListeners(type, ...args) {
                if (!this._listeners) {
                    return;
                }
                const handlers = this._listeners[type];
                if (handlers && handlers.length > 0) {
                    const handlersCopy = handlers.slice();
                    for (const handler of handlersCopy){
                        handler.handler.call(handler.options?.context || this, ...args);
                        if (handler.options.once) {
                            handler.removed = true;
                        }
                    }
                    for(let i = handlers.length - 1; i >= 0; i--){
                        if (handlers[i].removed) {
                            handlers.splice(i, 1);
                        }
                    }
                }
            }
        };
    };
}
/**
 * Cast augments a class with {@link IEventTarget} capabilities.
 *
 * It returns a higher-order factory that, when instantiated with an event map `X`,
 * produces a subclass adding `on/once/off/dispatchEvent` and the internal listener management.
 *
 * Usage:
 * ```ts
 * class Base {}
 * const Eventful = makeObservable(Base)<{ 'ready': []; 'data': [number] }>();
 * const obj = new Eventful();
 * obj.on('data', (n) => console.log(n));
 * obj.dispatchEvent('data', 42);
 * ```
 *
 * @typeParam C - A constructor type to be extended (plain `ObjectConstructor` or a generic class)
 * @param cls - The base class to extend
 * @returns A generic factory that accepts an event map and returns an extended class
 * @public
 */ function castObservable(cls) {
    return function _() {
        return cls;
    };
}

export { Observable, castObservable, makeObservable };
//# sourceMappingURL=event.js.map
